import { ChatComposerController } from './chatComposerInterface';
import { ChatComposerViewProps } from './chatComposerProps';
import { ReportingShellControllerBaseConfig } from '../../../../base';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { ContextItemProps } from '../context';
import { ThreadContextItem } from '@varos/assistant-sdk';
import { assertNever } from '@varos/util-typescript';
import { GoQuestion, GoTasklist } from 'react-icons/go';

export function createChatComposerController(
    config: ReportingShellControllerBaseConfig
): ChatComposerController {
    const {
        kernel: {
            provider: { createFormController },
        },
    } = config;

    function buildContextItemProps(context: ThreadContextItem): ContextItemProps {
        if (context.kind === 'reference') {
            return {
                id: context.document,
                label: context.document,
                Icon: HiOutlineDocumentText,
                tooltip: {
                    label: `Included document`,
                },
            };
        }
        if (context.kind === 'root') {
            return {
                id: context.document,
                label: context.document,
                Icon: HiOutlineDocumentText,
                tooltip: {
                    label: `Root document`,
                },
            };
        }
        if (context.kind === 'prompt') {
            return {
                id: 'prompt',
                label: 'Prompt',
                Icon: GoQuestion,
                tooltip: {
                    label: context.prompt,
                },
            };
        }
        if (context.kind === 'task') {
            return {
                id: 'task',
                label: 'Tasks',
                Icon: GoTasklist,
                tooltip: {
                    label: context.steps.map((step) => step.description).join(', '),
                },
            };
        }
        assertNever(context);
    }

    return {
        useProps(context, item, props): ChatComposerViewProps {
            const isLoading = props.form.isSubmitting;

            const selected = {
                mode: props.mode.options.find((option) => option.id === props.mode.value),
            };

            const isValid = props.text.value.trim().length > 0;
            const isSubmittable = isValid && !isLoading;

            return {
                isLoading: isLoading,
                inputRef: props.ref.composer,
                input: {
                    isDisabled: isLoading,
                    value: props.text.value,
                    onChange(event) {
                        props.text.onChange(event.target.value);
                    },
                    onKeyDown(event) {
                        if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            event.stopPropagation();
                        }
                    },
                    async onKeyUp(event) {
                        if (event.key === 'Enter' && !event.shiftKey && isSubmittable) {
                            props.form.onSubmit({
                                text: props.text.value,
                            });
                        }
                    },
                },
                submit: {
                    button: {
                        onClick() {
                            props.form.onSubmit({
                                text: props.text.value,
                            });
                        },
                        isDisabled: !isSubmittable,
                    },
                },
                mode: selected.mode
                    ? {
                          label: selected.mode.name,
                          Icon: selected.mode.Icon,
                          options: props.mode.options.map((option) => ({
                              id: option.id,
                              label: option.name,
                              description: option.description,
                              Icon: option.Icon,
                              button: {
                                  onClick: () => props.mode.onChange(option.id),
                              },
                          })),
                      }
                    : null,
                context: item.thread
                    ? {
                          items: item.thread?.context.map(buildContextItemProps),
                      }
                    : null,
            };
        },
    };
}
